import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { UtilService } from '@service/util/util.service';
import { UrlService } from '@service/url/url.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import {
	AccountDetailsView,
	ADD_DAYS,
	DATE_TIME,
	DATE_YMD,
	IS_LESS_THAN_CURRENT_TIMESTAMP,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	RestService,
	IS_VALID_DATE
} from 'cargo-chief-commons';
import { PLAN_NAMES } from '@domain/constants/app-variables';
import { Cargochief } from '@service/cargochief/cargochief.service';
import moment from 'moment';
import { environment } from '@environment/environment';

@Component({
	selector: 'app-edit-plan',
	templateUrl: './edit-plan.component.html',
	styleUrls: ['./edit-plan.component.scss'],
})
export class EditPlanComponent implements OnInit {
	@Input() accountDetails: AccountDetailsView = null;
	@Input() account = null;

	@Output() updatedPlan: EventEmitter<any> = new EventEmitter();
	@Output() updatedCreditCard: EventEmitter<boolean> = new EventEmitter();
	@Output() freeTrialDate: EventEmitter<string> = new EventEmitter();
	@Output() updateNextPaymentDate: EventEmitter<string> = new EventEmitter();

	constructor(public utilService: UtilService, public dialog: MatDialog) {}

	ngOnInit() {}

	openDialog() {
		const dialogRef = this.dialog.open(EditPlanDialog, {
			width: '450px',
			data: {
				accountDetails: this.accountDetails,
				account: this.account,
			},
		});
		dialogRef.afterClosed().subscribe((result) => {
			if (!this.utilService.isNullOrUndefined(result)) {
				this.updatedPlan.emit(result);
			}
		});
		dialogRef.componentInstance.updatedCreditCard.subscribe((result) => {
			this.updatedCreditCard.emit(result);
		});
		dialogRef.componentInstance.freeTrialDate.subscribe((result) => {
			this.freeTrialDate.emit(result);
		});
		dialogRef.componentInstance.updateNextPaymentDate.subscribe((result) => {
			this.updateNextPaymentDate.emit(result);
		});
	}
}

@Component({
	selector: 'app-edit-plan-dialog',
	templateUrl: 'modal.html',
})
export class EditPlanDialog {
	planDetails: UntypedFormGroup = null;
	isUpdatingPlan: boolean = false;
	listOfPlans = null;
	planNames = null;
	mindate = new Date();
	currentPlanMsg: string = '';
	IS_NOT_EMPTY = IS_NOT_EMPTY;
	IS_NOT_NULL_OR_UNDEFINED = IS_NOT_NULL_OR_UNDEFINED;
	creditCardPayment: boolean = false;
	initialFormValue: any = {};
	isPayingStripe: boolean = false;

	VALID_DATE_FORMAT = ['Y-M-D'];

	@Output() updatedCreditCard: EventEmitter<boolean> = new EventEmitter();
	@Output() freeTrialDate: EventEmitter<string> = new EventEmitter();
	@Output() updateNextPaymentDate: EventEmitter<string> = new EventEmitter();

	constructor(
		public dialogRef: MatDialogRef<EditPlanDialog>,
		@Inject(MAT_DIALOG_DATA) public data: any,
		private urlService: UrlService,
		public utilService: UtilService,
		private restService: RestService,
		private _formBuilder: UntypedFormBuilder,
		private cargochief: Cargochief,
		public dialog: MatDialog
	) {
		this.getPlanList();
		if (IS_NOT_NULL_OR_UNDEFINED(this.data.account.subscription.creditCardPayment)) {
			this.creditCardPayment = this.data.account.subscription.creditCardPayment;
		}
	}

	closeDialog(data: any): void {
		this.dialogRef.close(data);
	}

	getPlanList() {
		const url = this.urlService.getPlanList();
		this.restService.GET(url).subscribe(
			(result) => {
				this.listOfPlans = result;
				this.planNames = this.listOfPlans.map((plan) => {
					return plan.displayName;
				});
				this.initializeForm();
			},
			(error) => {
				this.utilService.showSnackbar(
					'Unable to load active plans list. Please try again later.',
					SNACKBAR.ERROR,
					SNACKBAR.ACTION
				);
			}
		);
	}

	initializeForm() {
		const selectedPlan = this.listOfPlans.filter((plan) => {
			if (plan.displayName == this.data.accountDetails.plan) {
				return plan;
			}
		})[0].displayName;
		this.planDetails = this._formBuilder.group({
			currentPlan: [selectedPlan, Validators.required],
			freeTrial: true,
			freeTrialDate: new Date(ADD_DAYS(new Date(), environment.FREE_TRIAL_DAYS)),
			emailUser: false,
			creditCardPayment: this.creditCardPayment,
		});
		this.updatePlanChange();
		this.updateTrialEnd(this.data.account.stripeTrialEnd, this.data.account?.subscription?.lastPaymentDate);
		this.initialFormValue = Object.assign({}, this.planDetails.value);
	}

	updateTrialEnd(stripeTrialEnd: number, lastPaymentDate: Date) {
		this.isPayingStripe = false;
		if (IS_NOT_NULL_OR_UNDEFINED(stripeTrialEnd)) {
			const trialEnd = moment(stripeTrialEnd * 1000).toDate();
			if (IS_LESS_THAN_CURRENT_TIMESTAMP(DATE_YMD(trialEnd))) {
				this.updateStripePaymentData();
			}
		} else if(IS_NOT_NULL_OR_UNDEFINED(lastPaymentDate)) {
			this.updateStripePaymentData();
		}
		if (IS_VALID_DATE(DATE_YMD(this.data?.accountDetails?.freeTrial), this.VALID_DATE_FORMAT)) {
			this.planDetails
				.get('freeTrialDate')
				.setValue(moment(this.data.accountDetails.freeTrial).toDate());
		} else if(IS_VALID_DATE(DATE_YMD(this.data?.account?.freeTrial)	, this.VALID_DATE_FORMAT)) {
			this.planDetails.get('freeTrial').setValue(true);
			this.planDetails
				.get('freeTrialDate')
				.setValue(moment(this.data?.account?.freeTrial).toDate());
		} else {
			this.planDetails.get('freeTrial').setValue(false);
			this.planDetails.get('freeTrialDate').setValue(null);
		}
	}

	updateStripePaymentData(): void {
		this.isPayingStripe = true;
		this.planDetails.get('freeTrial').setValue(false);
		this.planDetails.get('freeTrial').disable();
		this.currentPlanMsg = `This is paying account.`;
	}

	updatePlanConfirmationDialog() {
		const selectedPlan: any = this.planDetails.get('currentPlan').value;
		const plan: any = this.listOfPlans.filter((plan) => {
			return plan.displayName == selectedPlan;
		})[0];
		this.updatePlan();
	}

	updatePlan() {
		this.isUpdatingPlan = true;
		const url = this.urlService.updateSubscriptionStatusUrl(
			this.data.accountDetails.subscriptionId
		);
		const payload = this.buildPayload();
		this.restService.PUT(url, payload).subscribe(
			(result) => {
				this.updateSubscriptionStatusToast(result, payload);
			},
			(error) => {
				this.updateSubscriptionStatusToast(error, payload);
			}
		);
	}

	updateSubscriptionStatusToast(response: any, requestPayload: any = null) {
		if (response.status == 200) {
			this.utilService.showSnackbar(
				'Subscription status updated successfully.',
				SNACKBAR.SUCCESS,
				SNACKBAR.ACTION
			);
			this.updatedCreditCard.emit(this.planDetails.get('creditCardPayment').value);
			if (IS_NOT_NULL_OR_UNDEFINED(requestPayload)) {
				const updatedDate: string = requestPayload?.freeTrial ? requestPayload.freeTrial : 'N/A';
				this.freeTrialDate.emit(updatedDate);
				if (!this.isPayingStripe && IS_NOT_NULL_OR_UNDEFINED(this.data.account.stripeTrialEnd)) {
					this.updateNextPaymentDate.emit(updatedDate);
				}
			}
			this.closeDialog(this.planDetails.get('currentPlan').value);
		} else {
			let errorMsg: string = `Unable to update subscription status now. Please try again later.`;
			if (IS_NOT_EMPTY(response.error)) {
				errorMsg = response.error;
				errorMsg = errorMsg.replace(
					requestPayload.newPlan,
					this.planDetails.get('currentPlan').value
				);
			}
			this.utilService.showSnackbar(errorMsg, SNACKBAR.ERROR, SNACKBAR.ACTION);
		}
		this.isUpdatingPlan = false;
	}

	buildPayload(): any {
		const selectedPlan = this.planDetails.get('currentPlan').value,
			isFreeTrial = this.planDetails.get('freeTrial').value, //DATE_YMD(DATE_TIME((this.planDetails.get('freeTrialDate').value))) ==  DATE_YMD(DATE_TIME(new Date())) ? false : this.planDetails.get('freeTrial').value,
			IS_DOWNGRADE_PLAN = false, //this.cargochief.isProductEnabled(PLAN_PRODUCTS.CARRIER_RECRUITING);
			isSamePlan = selectedPlan == this.data.accountDetails.plan,
			oldPlan = this.listOfPlans.find((plan) => plan.displayName === this.data.accountDetails.plan),
			newPlan = this.listOfPlans.find((plan) => plan.displayName === selectedPlan);
		return {
			id: this.data.accountDetails.subscriptionId,
			planId: this.listOfPlans.filter((plan) => plan.displayName === selectedPlan)[0].id,
			sendEmail: isSamePlan ? false : this.planDetails.get('emailUser').value || isFreeTrial,
			freeTrialEmail: isFreeTrial,
			accountId: this.data.accountDetails.accountId,
			freeTrial: isFreeTrial
				? DATE_YMD(DATE_TIME(this.planDetails.get('freeTrialDate').value))
				: null,
			subscriptionUpgraded: isSamePlan ? false : !IS_DOWNGRADE_PLAN,
			subscriptionDowngraded: isSamePlan ? false : IS_DOWNGRADE_PLAN,
			oldPlan: oldPlan.name,
			newPlan: newPlan.name,
			creditCardPayment: this.planDetails.get('creditCardPayment').value,
		};
	}

	updatePlanChange() {
		this.planDetails.get('currentPlan').valueChanges.subscribe((planSelected) => {
			switch (planSelected.toLowerCase()) {
				case PLAN_NAMES.LITE.toLowerCase():
					this.planDetails.get('freeTrial').setValue(false);
					this.planDetails.get('freeTrial').disable();
					this.currentPlanMsg = 'No Free trial required for LITE plan as its free.';
					break;
				default:
					this.currentPlanMsg = '';
					this.planDetails.get('freeTrial').enable();
					break;
			}
			const plan: any = this.listOfPlans.filter((plan) => {
				return plan.displayName == planSelected;
			})[0];
			if (
				!this.utilService.isNullOrUndefined(plan.stripePlanId) &&
				!this.utilService.isNullOrUndefined(plan.price) &&
				this.utilService.isNullOrUndefined(this.data.account.subscription.stripeSubscriptionId)
			) {
				this.planDetails.get('freeTrial').setValue(false);
				this.planDetails.get('freeTrial').disable();
				this.currentPlanMsg =
					'You can not set free trial intially as this account have not put their credit card info. You can only change plan without free trial.';
			}
			if (this.data?.accountDetails?.plan === planSelected) {
				this.updateTrialEnd(this.data.account.stripeTrialEnd, this.data.account?.subscription?.lastPaymentDate);
			}
		});
	}

	isFormValueChanged(): boolean {
		return JSON.stringify(this.initialFormValue) != JSON.stringify(this.planDetails.value);
	}
}
